// File: /opt/dev_environment/assets/workspace/app-init/src/services/stagedVerbApi.js

/**
 * Staged Verb API Client
 * Handles all interactions with the staged verb conjugation endpoints.
 * Now includes Bearer token authentication via the shared request function.
 */

import AuthService from './authService';

const API_BASE = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
const BASE_URL = `${API_BASE}/api/v1`;

class StagedVerbApiError extends Error {
  constructor(message, status, data) {
    super(message);
    this.status = status;
    this.data = data;
    this.name = 'StagedVerbApiError';
  }
}

/**
 * Get a valid access token, refreshing if necessary.
 */
async function getValidAccessToken() {
  let accessToken = AuthService.getAccessToken();

  if (!accessToken) {
    const refreshToken = AuthService.getRefreshToken();
    if (refreshToken) {
      try {
        accessToken = await AuthService.refreshAccessToken();
      } catch {
        return null;
      }
    }
  }

  return accessToken;
}

async function request(endpoint, options = {}) {
  const url = `${BASE_URL}${endpoint}`;

  // Get a valid access token
  let accessToken = await getValidAccessToken();

  const config = {
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    credentials: 'same-origin',
    ...options,
  };

  // Add Authorization header if we have a token
  if (accessToken) {
    config.headers['Authorization'] = `Bearer ${accessToken}`;
  }

  try {
    let response = await fetch(url, config);

    // Handle 401 — try to refresh token and retry once
    if (response.status === 401 && AuthService.getRefreshToken()) {
      try {
        const newToken = await AuthService.refreshAccessToken();
        config.headers['Authorization'] = `Bearer ${newToken}`;
        response = await fetch(url, config);

        if (response.status === 401) {
          AuthService.clearTokens();
          window.dispatchEvent(new CustomEvent('auth:session-expired'));
          throw new StagedVerbApiError('Sitzung abgelaufen', 401, null);
        }
      } catch (refreshError) {
        if (refreshError instanceof StagedVerbApiError) throw refreshError;
        AuthService.clearTokens();
        window.dispatchEvent(new CustomEvent('auth:session-expired'));
        throw new StagedVerbApiError('Sitzung abgelaufen', 401, null);
      }
    }

    if (response.status === 204) {
      return null;
    }

    const data = await response.json().catch(() => null);

    if (!response.ok) {
      throw new StagedVerbApiError(
        data?.detail || data?.error || `API error: ${response.status} ${response.statusText}`,
        response.status,
        data
      );
    }

    return data;
  } catch (error) {
    if (error instanceof StagedVerbApiError) throw error;
    throw new StagedVerbApiError(
      `Network error: ${error.message}`,
      0,
      null
    );
  }
}

function buildQueryString(params) {
  const filtered = Object.entries(params).filter(
    ([, v]) => v !== undefined && v !== null && v !== ''
  );
  if (filtered.length === 0) return '';
  return '?' + filtered.map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`).join('&');
}

export const stagedVerbApi = {
  /**
   * Check if a verb already exists before adding.
   * @param {string} userInput - The verb form entered by the user
   * @returns {Promise<{exists: boolean, existing_verb: object|null, similar_verbs: object[]}>}
   */
  async checkVerb(userInput) {
    return request('/staged-verbs/check', {
      method: 'POST',
      body: JSON.stringify({ user_input: userInput }),
    });
  },

  /**
   * Add a new verb for LLM analysis and conjugation generation.
   * @param {string} userInput - The verb form entered by the user
   * @returns {Promise<{status: string, message: string, verb: object}>}
   */
  async addVerb(userInput) {
    return request('/staged-verbs', {
      method: 'POST',
      body: JSON.stringify({ user_input: userInput }),
    });
  },

  /**
   * List staged verbs with optional filters.
   * @param {object} params - { status?, limit?, offset? }
   * @returns {Promise<{verbs: object[], total: number}>}
   */
  async listVerbs(params = {}) {
    const qs = buildQueryString(params);
    return request(`/staged-verbs${qs}`);
  },

  /**
   * Get detailed information for a single staged verb.
   * @param {string} verbId
   * @returns {Promise<object>}
   */
  async getVerb(verbId) {
    return request(`/staged-verbs/${verbId}`);
  },

  /**
   * Update a staged verb (selected forms, corrections, etc.).
   * @param {string} verbId
   * @param {object} data - { selected_forms?, german_infinitive?, conjugation_corrections? }
   * @returns {Promise<object>}
   */
  async updateVerb(verbId, data) {
    return request(`/staged-verbs/${verbId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  /**
   * Delete a staged verb.
   * @param {string} verbId
   */
  async deleteVerb(verbId) {
    return request(`/staged-verbs/${verbId}`, {
      method: 'DELETE',
    });
  },

  /**
   * Retry processing for a verb that had an error.
   * @param {string} verbId
   * @returns {Promise<object>}
   */
  async reprocessVerb(verbId) {
    return request(`/staged-verbs/${verbId}/reprocess`, {
      method: 'POST',
    });
  },

  /**
   * Import staged verbs as flashcards for training.
   * @param {object} importRequest - { verb_ids: string[], selected_forms?: string[], add_to_vocabulary?: boolean }
   * @returns {Promise<{imported_verbs: number, created_cards: number, vocabulary_cards_created: number, errors: object[]}>}
   */
  async importVerbs(importRequest) {
    return request('/staged-verbs/import', {
      method: 'POST',
      body: JSON.stringify(importRequest),
    });
  },
};

export { StagedVerbApiError };